/**
 * The flat surfaces: Card, Eyebrow, Notice, WarnBox, GapBox.
 *
 * WarnBox and GapBox look similar and mean different things, so they are two
 * components rather than one with a `tone` prop:
 *
 *   WarnBox — amber. You may proceed anyway. Since D-033 (R16) it no longer
 *             carries the §2.4 photo sentence on section screens; the Room
 *             Setup builder and the office screen still say their one amber
 *             thing with it.
 *   GapBox  — red. Something WILL travel with the submitted record and land on
 *             Chip's and Tim's desk (§2.3: the surveyor cannot self-clear it).
 */

import type { ReactNode } from 'react';

export interface CardProps {
  children: ReactNode;
  /** Tighter padding for a card that is a list of rows. */
  flush?: boolean;
  as?: 'div' | 'section';
  ariaLabel?: string;
}

export function Card({ children, flush = false, as = 'div', ariaLabel }: CardProps): ReactNode {
  const className = flush ? 'card card--flush' : 'card';
  if (as === 'section') {
    return (
      <section className={className} aria-label={ariaLabel}>
        {children}
      </section>
    );
  }
  return (
    <div className={className} aria-label={ariaLabel}>
      {children}
    </div>
  );
}

export interface EyebrowProps {
  children: ReactNode;
}

/** The small uppercase divider that names the block below it. */
export function Eyebrow({ children }: EyebrowProps): ReactNode {
  return <h2 className="eyebrow" data-legibility="display">{children}</h2>;
}

export interface NoticeProps {
  children: ReactNode;
  icon?: string;
}

/**
 * Standing reassurance — offline safety, how repetition works. Never an alert.
 *
 * `helper` by declaration: this is the component that carries "tap the cars that
 * run on ATS" and "the form walks top to bottom". Standard item 6 — that is
 * content telling the surveyor what to do, so it is held to >= 16px, weight
 * >= 500 and full AA contrast, with no large-text discount.
 */
export function Notice({ children, icon = '▲' }: NoticeProps): ReactNode {
  return (
    <p className="notice" data-legibility="helper">
      <span className="notice__icon" aria-hidden="true">
        {icon}
      </span>
      <span>{children}</span>
    </p>
  );
}

export interface WarnBoxProps {
  children: ReactNode;
}

/** Amber: you may proceed. */
export function WarnBox({ children }: WarnBoxProps): ReactNode {
  return (
    <div className="warnbox" role="status" aria-live="polite">
      {children}
    </div>
  );
}

export interface GapBoxProps {
  children: ReactNode;
}

/** Red: this travels with the record to Chip and Tim. */
export function GapBox({ children }: GapBoxProps): ReactNode {
  return (
    <div className="gapbox" role="status" aria-live="polite">
      <span aria-hidden="true">⚑</span>
      <span>{children}</span>
    </div>
  );
}
